import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '@/services/api';
import { CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import ThemeToggle from '@/components/shared/ThemeToggle';

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Missing verification token.');
      return;
    }
    (async () => {
      try {
        await authAPI.verifyEmailToken(token);
        setStatus('success');
      } catch (err) {
        setStatus('error');
        setError(err?.response?.data?.detail || 'Verification failed');
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] flex items-center justify-center px-6" data-testid="verify-email-page">
      <ThemeToggle className="fixed top-4 right-4 z-50" />
      <div className="w-full max-w-md text-center">
        {status === 'loading' && (
          <div data-testid="verify-email-loading">
            <Loader2 className="w-8 h-8 text-slate-400 dark:text-zinc-500 mx-auto mb-4 animate-spin" />
            <p className="text-slate-500 dark:text-zinc-400 text-sm">Verifying your email…</p>
          </div>
        )}
        {status === 'success' && (
          <div data-testid="verify-email-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
              <span className="text-slate-900 dark:text-white">Email </span>
              <span className="text-emerald-600 dark:text-emerald-400">verified</span>
            </h1>
            <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">You&apos;re all set. You can close this tab or head back to your dashboard.</p>
            <Link to="/login" data-testid="verify-email-continue-link" className="inline-block bg-slate-900 dark:bg-white text-white dark:text-black font-medium px-6 py-2.5 rounded-lg hover:bg-slate-800 dark:hover:bg-zinc-200 transition-colors">
              Continue to Numuv
            </Link>
          </div>
        )}
        {status === 'error' && (
          <div data-testid="verify-email-error">
            <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2 text-slate-900 dark:text-white">Verification failed</h1>
            <p className="text-red-600 dark:text-red-400 text-sm mb-6">{error}</p>
            <Link to="/login" className="inline-block bg-slate-900 dark:bg-white text-white dark:text-black font-medium px-6 py-2.5 rounded-lg hover:bg-slate-800 dark:hover:bg-zinc-200 transition-colors">
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
